import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltIconCogOutline } from '@cae/cobalt-react/icons/outline/CobaltIconCogOutline';
import { CobaltIconCheckmarkCircleSolid } from '@cae/cobalt-react/icons/solid/CobaltIconCheckmarkCircleSolid';
import {
  classNames,
  CobaltDynamicTruncateString,
} from '@cae/cobalt-react/utils';
import * as React from 'react';

import type { OrganizationOptionProps } from './OrganizationOption.types';

import styles from './OrganizationOption.module.css';

const OrganizationOption = React.memo(
  ({
    organization,
    isSelected,
    isActive,
    isInternalUser,
    showCompanyProfile,
    index,
    onSelect,
    onCompanyProfileClick,
    onSetActiveIndex,
  }: Readonly<OrganizationOptionProps>): React.ReactElement => {
    const optionRef = React.useRef<HTMLLIElement>(null);

    React.useEffect(() => {
      if (isActive && optionRef.current) {
        optionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }, [isActive]);
    const handleClick = React.useCallback((): void => {
      onSelect(organization);
    }, [onSelect, organization]);

    const handleCompanyProfileClick = React.useCallback((): void => {
      onCompanyProfileClick(organization.customerId);
    }, [onCompanyProfileClick, organization.customerId]);

    const handleMouseEnter = React.useCallback((): void => {
      if (!isSelected) {
        onSetActiveIndex(index);
      }
    }, [onSetActiveIndex, index, isSelected]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent): void => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(organization);
        }
      },
      [onSelect, organization]
    );

    const optionClassName = classNames(
      styles.organizationOption,
      isInternalUser && styles.organizationOptionInternal,
      isActive && !isSelected && styles.organizationOptionActive,
      isSelected && styles.organizationOptionSelected
    );

    return (
      <li
        ref={optionRef}
        id={`org-option-${organization.id}`}
        role="option"
        aria-selected={isSelected}
        tabIndex={-1}
        className={optionClassName}
        onClick={!isSelected ? handleClick : undefined}
        onKeyDown={!isSelected ? handleKeyDown : undefined}
        onMouseEnter={handleMouseEnter}
        data-testid={`organization-option-${organization.id}`}
      >
        <div className={styles.organizationOptionContent}>
          <CobaltDynamicTruncateString maxLines={2} className="body-02">
            {organization.name}
          </CobaltDynamicTruncateString>
          {!isInternalUser && organization.roles.length > 0 && (
            <span className={`${styles.organizationRoles} body-01`}>
              {isSelected && (
                <CobaltIconCheckmarkCircleSolid
                  className={styles.organizationSelected}
                />
              )}
              <span className={styles.organizationRolesText}>
                {organization.roles.join(', ')}
              </span>
            </span>
          )}
        </div>

        {isSelected && showCompanyProfile && (
          <CobaltButton
            variant="ghost"
            size="small"
            onClick={handleCompanyProfileClick}
            aria-label="Company profile settings"
            data-testid="company-profile-button"
          >
            <CobaltIconCogOutline />
          </CobaltButton>
        )}
      </li>
    );
  }
);

OrganizationOption.displayName = 'OrganizationOption';

export { OrganizationOption };
